import type { OverviewWeldRow } from '../components/WeldOverviewTable';
import type { PipeJobRef } from './jobNumbersFromSetup';
import { pipeJobRefKey } from './jobNumbersFromSetup';

export type WeldRowWithPipe = OverviewWeldRow & {
  pipeId?: string | number | null;
  pipe_details_id?: string | number | null;
};

function rowJobNumbers(row: OverviewWeldRow): string[] {
  const jn = row.job_number;
  if (jn == null) return [];
  if (Array.isArray(jn)) return jn.map((j) => String(j).trim()).filter(Boolean);
  const s = String(jn).trim();
  return s ? [s] : [];
}

function rowPipeId(row: WeldRowWithPipe): string | null {
  const raw = row.pipeId ?? row.pipe_details_id;
  if (raw == null || raw === '') return null;
  return String(raw);
}

/** True when the weld row belongs to at least one of the selected job numbers. */
export function weldRowMatchesJobSelection(row: OverviewWeldRow, selectedJobs: string[]): boolean {
  if (selectedJobs.length === 0) return false;
  const rowJobs = rowJobNumbers(row);
  if (rowJobs.length === 0) return false;
  const selected = new Set(selectedJobs.map((j) => j.trim()).filter(Boolean));
  return rowJobs.some((j) => selected.has(j));
}

/**
 * Match production rows to pipe–job refs.
 * Prefer (pipeId, job) when the row is pipe-tagged; fall back to job-only when pipe is missing.
 */
export function weldRowMatchesJobRefs(row: WeldRowWithPipe, jobRefs: readonly PipeJobRef[]): boolean {
  if (jobRefs.length === 0) return false;
  const rowJobs = rowJobNumbers(row);
  if (rowJobs.length === 0) return false;
  const pipeId = rowPipeId(row);
  if (pipeId == null) {
    const jobs = new Set(jobRefs.map((r) => r.jobNumber.trim()).filter(Boolean));
    return rowJobs.some((j) => jobs.has(j));
  }
  const selected = new Set(jobRefs.map(pipeJobRefKey));
  return rowJobs.some((j) => selected.has(pipeJobRefKey({ pipeId, jobNumber: j })));
}

/** Filter production weld rows to those matching any selected job number. */
export function filterWeldsByJobNumbers<T extends OverviewWeldRow>(
  rows: readonly T[],
  selectedJobs: string[],
): T[] {
  if (selectedJobs.length === 0) return [];
  return rows.filter((row) => weldRowMatchesJobSelection(row, selectedJobs));
}

/** Filter production weld rows by pipe–job refs (pipe-aware when tagged). */
export function filterWeldsByJobRefs<T extends WeldRowWithPipe>(
  rows: readonly T[],
  jobRefs: readonly PipeJobRef[],
): T[] {
  if (jobRefs.length === 0) return [];
  return rows.filter((row) => weldRowMatchesJobRefs(row, jobRefs));
}

/** Stable key for comparing job multiselect vs last-applied set. */
export function jobNumbersKey(jobs: string[]): string {
  return [...jobs].map((j) => j.trim()).filter(Boolean).sort().join('|');
}
